import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { MentionsInput } from './mentions-input';
import { SubsetFact } from '../../../../../backend/src/types/types';
import { useCreateComment } from '../../../api/mutations/useCreateFactComment';
import { useDeleteComment } from '../../../api/mutations/useDeleteComment';
import { useUpdateComment } from '../../../api/mutations/useUpdateComment';
import { useGetUsersInOrg } from '../../../api/queries/useGetUsersInOrg';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../shadcn/dialog';
import { FactDetails } from '@/api/types/backend-dto-types';
import { renderHighlightedTextWithLinks } from '@/helpers/textRendering';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Fact } from '@/types';

type FactWithComments = Fact | SubsetFact | FactDetails;

interface CommentSectionProps {
  fact: FactWithComments;
}

export function CommentSection({ fact }: CommentSectionProps) {
  const { matterId, subsetId } = useParams();

  const [showAllComments, setShowAllComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: userData } = useGetUsersInOrg();
  const { mutate: createComment, isLoading: isSubmitting } = useCreateComment(matterId ?? '', fact.id, subsetId);
  const { mutate: updateComment, isLoading: isUpdating } = useUpdateComment(
    matterId ?? '',
    editingCommentId ?? '',
    subsetId,
  );
  const { mutate: deleteComment } = useDeleteComment(matterId ?? '', subsetId);

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;

    createComment(
      { comment: newComment },
      {
        onSuccess: () => {
          setNewComment('');
        },
      },
    );
  };

  const handleEditClick = (commentId: string, currentContent: string) => {
    setEditingCommentId(commentId);
    setEditingCommentContent(currentContent);
    setIsEditDialogOpen(true);
  };

  const handleUpdateComment = () => {
    if (!editingCommentContent.trim() || !editingCommentId) return;

    updateComment(
      { content: editingCommentContent },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingCommentId(null);
          setEditingCommentContent('');
        },
      },
    );
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  return (
    <div className="flex grow flex-col">
      <p className="font-medium">Comments</p>

      {(fact.comments && fact.comments.length) > 0 ? (
        <div className="mt-3.5 flex flex-col gap-2 text-xs">
          {fact.comments
            .toReversed()
            .slice(0, showAllComments ? undefined : 2)
            .map((c) => (
              <p key={c.id} className="flex justify-between gap-2">
                <span className="flex gap-1.5">
                  <span className="text-gray-500">{c.createdBy?.email ?? 'Unknown'}:</span>
                  <span>{renderHighlightedTextWithLinks(c.content)}</span>
                </span>
                <span className="flex gap-2 whitespace-nowrap text-gray-500">
                  {new Date(c.createdDate).toLocaleString()}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="h-min p-0.5" size="sm">
                        <Icon className="lucide--more-horizontal" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>
                        <Dialog open={isEditDialogOpen && editingCommentId === c.id} onOpenChange={setIsEditDialogOpen}>
                          <DialogTrigger
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(c.id, c.content);
                            }}
                          >
                            Edit comment
                          </DialogTrigger>
                          <DialogContent onKeyDown={(e) => e.key === ' ' && e.stopPropagation()}>
                            <DialogHeader>
                              <DialogTitle>Edit Comment</DialogTitle>
                            </DialogHeader>

                            <MentionsInput
                              value={editingCommentContent}
                              onChange={setEditingCommentContent}
                              placeholder="Edit your comment (type @ to mention other users)"
                              users={userData}
                              onSubmit={handleUpdateComment}
                              disabled={isUpdating}
                            />

                            <div className="mt-4 flex justify-end gap-2">
                              <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                onClick={handleUpdateComment}
                                disabled={
                                  isUpdating || !editingCommentContent.trim() || editingCommentContent === c.content
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-red-500" onClick={() => deleteComment({ commentId: c.id })}>
                        Delete comment
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </p>
            ))}

          {fact.comments.length > 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllComments(!showAllComments)}
              className="mt-1 self-start px-0 py-1 font-normal text-blue-600 hover:text-blue-700"
            >
              {showAllComments
                ? 'Show fewer comments'
                : `Show ${fact.comments.length - 2} more comment${fact.comments.length - 2 !== 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
      ) : (
        <span className="mt-1 text-xs text-gray-500">No comments yet</span>
      )}

      <div className="mt-3 flex gap-2">
        <MentionsInput
          value={newComment}
          onChange={setNewComment}
          placeholder="Add a comment (type @ to mention other users)"
          users={userData}
          onSubmit={handleSubmitComment}
          disabled={isSubmitting}
        />

        <Button onClick={handleSubmitComment} disabled={isSubmitting || !newComment.trim()}>
          {isSubmitting ? (
            <Icon className="lucide--loader-circle animate-spin" />
          ) : (
            <Icon className="lucide--arrow-right" />
          )}
        </Button>
      </div>
    </div>
  );
}
